import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import {
    HELPDESK_STATUSES,
    HELPDESK_STATUS_LABEL,
    HELPDESK_STATUS_TONE,
    formatBytes,
    formatDate,
    type HelpdeskAnexo,
    type HelpdeskStatus,
    type HelpdeskTicketDetail,
} from '../helpdeskModel'

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }} component="div">{children}</Typography>
        </Box>
    )
}

export default function HelpdeskTicketDetail() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { notify } = useNotification()

    const [ticket, setTicket] = useState<HelpdeskTicketDetail | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')
    const [savingStatus, setSavingStatus] = useState(false)

    const [anexos, setAnexos] = useState<HelpdeskAnexo[]>([])
    const [anexosError, setAnexosError] = useState('')
    const [downloadingId, setDownloadingId] = useState<string | null>(null)

    useEffect(() => {
        if (!id) return
        let cancelled = false
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset before the two fetches below
        setIsLoading(true)
        setError('')
        setAnexos([])
        setAnexosError('')

        api.get<HelpdeskTicketDetail>(`/api/admin/helpdesk/tickets/${id}`)
            .then((r) => { if (!cancelled) setTicket(r.data) })
            .catch(() => { if (!cancelled) setError('Não foi possível carregar o pedido de suporte.') })
            .finally(() => { if (!cancelled) setIsLoading(false) })

        api.get<HelpdeskAnexo[]>(`/api/admin/helpdesk/tickets/${id}/anexos`)
            .then((r) => { if (!cancelled) setAnexos(Array.isArray(r.data) ? r.data : []) })
            .catch(() => { if (!cancelled) setAnexosError('Não foi possível carregar os anexos.') })

        return () => { cancelled = true }
    }, [id])

    const downloadAnexo = async (anexo: HelpdeskAnexo) => {
        setDownloadingId(anexo.id)
        try {
            const r = await api.get(`/api/admin/helpdesk/tickets/${id}/anexos/${anexo.id}`, { responseType: 'blob' })
            const url = URL.createObjectURL(r.data as Blob)
            const a = document.createElement('a')
            a.href = url
            a.download = anexo.nomeOriginal || 'anexo'
            document.body.appendChild(a)
            a.click()
            a.remove()
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch {
            notify('Não foi possível descarregar o anexo.', 'error')
        } finally {
            setDownloadingId(null)
        }
    }

    const changeStatus = async (next: HelpdeskStatus) => {
        if (!ticket || next === ticket.status) return
        setSavingStatus(true)
        try {
            const r = await api.patch<HelpdeskTicketDetail>(`/api/admin/helpdesk/tickets/${ticket.id}/status`, { status: next })
            setTicket(r.data)
            notify('Estado actualizado.')
        } catch {
            notify('Não foi possível alterar o estado.', 'error')
        } finally {
            setSavingStatus(false)
        }
    }

    return (
        <Box>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/helpdesk')}
                sx={{ color: 'text.secondary', pl: 0, mb: 2 }}
            >
                Voltar aos pedidos
            </Button>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            )}

            {error && !isLoading && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

            {ticket && !isLoading && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{ticket.titulo}</Typography>
                        <Chip
                            size="small"
                            label={HELPDESK_STATUS_LABEL[ticket.status]}
                            sx={{
                                bgcolor: HELPDESK_STATUS_TONE[ticket.status].bg,
                                color: HELPDESK_STATUS_TONE[ticket.status].color,
                                fontWeight: 600,
                            }}
                        />
                    </Box>

                    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid #E5E7EB', borderRadius: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <Field label="Empresa">
                                <Link component={RouterLink} to={`/tenants/${ticket.tenantId}`} underline="hover">
                                    {ticket.tenantNome || ticket.tenantId}
                                </Link>
                            </Field>
                            <Field label="Aberto por">{ticket.abertoPorNome || '—'}</Field>
                            <Field label="Email">
                                {ticket.abertoPorEmail
                                    ? <Link href={`mailto:${ticket.abertoPorEmail}`} underline="hover">{ticket.abertoPorEmail}</Link>
                                    : '—'}
                            </Field>
                            <Field label="Criado em">{formatDate(ticket.criadoEm)}</Field>
                            <Field label="Última actualização">{formatDate(ticket.atualizadoEm)}</Field>
                            <Field label="Fechado em">{formatDate(ticket.fechadoEm)}</Field>
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid #E5E7EB', borderRadius: 2, mb: 2.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Descrição</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{ticket.descricao}</Typography>
                    </Paper>

                    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid #E5E7EB', borderRadius: 2, mb: 2.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Anexos</Typography>
                        {anexosError ? (
                            <Typography variant="body2" color="error">{anexosError}</Typography>
                        ) : anexos.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">Sem anexos.</Typography>
                        ) : (
                            <Stack spacing={0.75}>
                                {anexos.map((anexo) => (
                                    <Box
                                        key={anexo.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            px: 1.25,
                                            py: 0.75,
                                        }}
                                    >
                                        <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{anexo.nomeOriginal}</Typography>
                                            {formatBytes(anexo.tamanhoBytes) && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatBytes(anexo.tamanhoBytes)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => downloadAnexo(anexo)}
                                            disabled={downloadingId === anexo.id}
                                            aria-label="Descarregar anexo"
                                        >
                                            {downloadingId === anexo.id
                                                ? <CircularProgress size={14} />
                                                : <DownloadRoundedIcon fontSize="small" />}
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Paper>

                    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid #E5E7EB', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Estado</Typography>
                        <TextField
                            select
                            size="small"
                            value={ticket.status}
                            onChange={(e) => changeStatus(e.target.value as HelpdeskStatus)}
                            disabled={savingStatus}
                            sx={{ width: { xs: '100%', sm: 260 } }}
                        >
                            {HELPDESK_STATUSES.map((s) => (
                                <MenuItem key={s} value={s}>{HELPDESK_STATUS_LABEL[s]}</MenuItem>
                            ))}
                        </TextField>
                    </Paper>
                </>
            )}
        </Box>
    )
}
